/**
 * Fanger e-postadresser som er skrevet feil, men som ser gyldige ut.
 *
 * En kunde skrev "online.nog" i stedet for "online.no". Skjemaet godtok den,
 * for formatet er helt korrekt, og bekreftelsen studset hos Resend uten at
 * hverken hun eller verkstedet fikk vite noe. Hun ringte etterpaa og lurte paa
 * hvorfor hun ikke hadde hoert noe.
 *
 * Dette blokkerer ingenting. En feilmelding som tar feil koster en booking,
 * mens et forslag kunden kan overse koster ingenting.
 */

/** Toppdomener norske kunder faktisk bruker. Alt utenfor er verdt et spoersmaal. */
const TOPPDOMENER = new Set([
  "no", "com", "net", "org", "se", "dk", "fi", "is", "uk", "de", "nl", "pl",
  "lt", "lv", "ee", "es", "it", "fr", "eu", "io", "me", "info", "biz", "live",
  "online", "email", "cloud", "app", "dev", "co", "us", "ru", "ua", "tr", "sy",
]);

/** Skrivefeil som gaar igjen. Verdien er det de nesten helt sikkert mente. */
const KJENTE_FEIL: Record<string, string> = {
  "gmial.com": "gmail.com",
  "gmai.com": "gmail.com",
  "gmail.co": "gmail.com",
  "gmail.con": "gmail.com",
  "gmail.om": "gmail.com",
  "hotmial.com": "hotmail.com",
  "hotmail.co": "hotmail.com",
  "hotmail.con": "hotmail.com",
  "outlok.com": "outlook.com",
  "outlook.con": "outlook.com",
  "yahoo.con": "yahoo.com",
  "icloud.con": "icloud.com",
  "online.nog": "online.no",
  "online.n": "online.no",
};

/** Ett tegn for mye, for lite eller feil. */
function énFeil(a: string, b: string): boolean {
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  let j = 0;
  let avvik = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i++;
      j++;
      continue;
    }
    if (++avvik > 1) return false;
    if (a.length > b.length) i++;
    else if (a.length < b.length) j++;
    else {
      i++;
      j++;
    }
  }
  return avvik + (a.length - i) + (b.length - j) <= 1;
}

/**
 * Returnerer en advarsel hvis adressen ser feilskrevet ut, ellers null.
 * `forslag` er satt naar vi tror vi vet hva kunden mente.
 */
export function sjekkEpost(epost: string): { melding: string; forslag?: string } | null {
  const ren = epost.trim().toLowerCase();
  const at = ren.lastIndexOf("@");
  if (at === -1) return null;

  const domene = ren.slice(at + 1);
  if (!domene.includes(".")) return null;

  const rettet = KJENTE_FEIL[domene];
  if (rettet) {
    return {
      melding: `Mente du ${ren.slice(0, at)}@${rettet}?`,
      forslag: `${epost.trim().slice(0, at)}@${rettet}`,
    };
  }

  const toppdomene = domene.slice(domene.lastIndexOf(".") + 1);
  if (TOPPDOMENER.has(toppdomene)) return null;

  // Ukjent toppdomene. Ligger det ett tegn unna et vi kjenner, si hva vi tror.
  for (const kjent of TOPPDOMENER) {
    if (énFeil(toppdomene, kjent)) {
      const nyttDomene = domene.slice(0, domene.lastIndexOf(".") + 1) + kjent;
      return {
        melding: `Mente du ${ren.slice(0, at)}@${nyttDomene}?`,
        forslag: `${epost.trim().slice(0, at)}@${nyttDomene}`,
      };
    }
  }

  return { melding: `Er ".${toppdomene}" riktig? Vi sender bekreftelsen hit.` };
}
